import React, { useCallback, useContext, useMemo, useState } from "react";
import * as yup from "yup";
import clsx from "clsx";
import { Formik, FormikHelpers } from "formik";
import { useTranslation } from "react-i18next";
import { useHistory, useParams } from "react-router-dom";
import {
  HvGrid,
  HvTooltip,
  HvTypography,
} from "@hitachivantara/uikit-react-core";
import {
  formatAMPM,
  getFormattedDateTimeWithTZ,
  standardDateFormat,
} from "lib/utils/datetime";

import { useSnackbar } from "lib/provider/SnackbarProvider";
import usePreventNavigation from "lib/hooks/usePreventNavigation";
import {
  deleteDevice,
  Device,
  editUser,
  UserDeviceAttribute,
  UserPhoneAttribute,
  useTimeSettings,
  useUser,
} from "lib/api";
import Container from "components/layout/Container";
import Loading from "components/layout/Loading";
import Banner from "components/common/Banner";
import { FormHeader } from "components/form";
import moment from "moment";
import { Settings, User } from "@hitachivantara/uikit-react-icons";
import { joinGroups } from "components/common/UserTable/utils";
import Details from "components/membersSettings/AddEdit/Details/Details";
import Devices from "components/membersSettings/AddEdit/Devices";
import { ModuleNames, ModulePolicyTypes } from "lib/api/userSettings";
import { UserDataContext } from "lib/context/UserDataContext";
import NotFound from "components/common/NotFound";
import useStyles from "./styles";

const Edit: React.FC = () => {
  const { t } = useTranslation("userAddEdit");
  const history = useHistory();
  const classes = useStyles();
  const { enqueueSnackbar } = useSnackbar();
  const { id } = useParams<{ id: string }>();
  const { timezone } = useTimeSettings();
  const [, setPreventNavigation] = usePreventNavigation();
  const [showBanner, setShowBanner] = useState(false);
  const [removedDevices, setRemovedDevices] = useState<UserDeviceAttribute[]>(
    []
  );

  const [errorBannerCode, setBannerErrorCode] = useState("");
  const { data: userData, mutate } = useUser(id);
  const sections = [
    { label: t("details.title"), value: "details" },
    { label: t("devices.title"), value: "devices" },
  ];

  const validUsername = useMemo(
    () =>
      userData?.data.is_integration
        ? yup
            .string()
            .trim()
            // eslint-disable-next-line no-empty-character-class
            .matches(/^[^<>(),:;@[\]]*$/, t("validation.userInvalidChars"))
            .required(t("validation.required"))
        : yup.string().trim().required(t("validation.required")),
    [t, userData?.data.is_integration]
  );
  const validationSchema = useMemo(
    () =>
      yup.object().shape({
        username: validUsername,
        password: yup.string().when({
          // eslint-disable-next-line camelcase
          is: (confirmation_password: string) =>
            // eslint-disable-next-line camelcase
            confirmation_password && confirmation_password.length,
          then: yup
            .string()
            .required(t("validation.required"))
            .oneOf(
              [yup.ref("confirmation_password"), null],
              t("validation.passwordMatch")
            ),
        }),
        confirmation_password: yup.string().when(["password", "old_password"], {
          // eslint-disable-next-line @typescript-eslint/no-explicit-any
          // eslint-disable-next-line camelcase
          is: (password: string, old_password: string) =>
            (password && password.length > 0) ||
            // eslint-disable-next-line camelcase
            (old_password && old_password.length > 0),
          then: yup
            .string()
            .required("Field is required")
            .oneOf([yup.ref("password"), null], t("validation.passwordMatch")),
        }),
        old_password: yup.string().when(["password"], {
          // eslint-disable-next-line @typescript-eslint/no-explicit-any
          // eslint-disable-next-line camelcase
          is: (password: string) => password && password.length > 0,

          then: yup.string().required("Field is required"),
        }),
        email: yup.string(),
      }),
    [t, validUsername]
  );

  const deleteDevices = useCallback(async () => {
    if (userData) {
      // eslint-disable-next-line no-restricted-syntax
      for (const device of removedDevices) {
        // eslint-disable-next-line no-await-in-loop
        await deleteDevice(userData?.data.id, {
          device_id: device.id,
          device_name: device.name,
        });
      }
      await mutate();
    }
  }, [mutate, removedDevices, userData]);

  const onSave = (data: UserDetailsFormData) => {
    const date = moment(data.expiration_date).format("YYYY-MM-DD");
    // eslint-disable-next-line camelcase
    const final_date = moment(
      `${date} ${data.expiration_time.hours}:${data.expiration_time.minutes}:${data.expiration_time.seconds}`
    ).format(standardDateFormat);

    const operations =
      data.password === data.confirmation_password &&
      data.old_password !== "" &&
      data.password !== "" &&
      data.confirmation_password !== ""
        ? {
            new_password: data.password,
            old_password: data.old_password,
            operations: [
              { op: "replace", path: "/username", value: data.username },
              { op: "replace", path: "/name", value: data.username },
              { op: "replace", path: "/surname", value: data.surname },
              {
                op: "replace",
                path: "/password_exp_timestamp",
                value: final_date,
              },
              { op: "replace", path: "/email", value: data.email },
            ],
          }
        : {
            operations: [
              { op: "replace", path: "/username", value: data.username },
              { op: "replace", path: "/name", value: data.username },
              { op: "replace", path: "/surname", value: data.surname },
              {
                op: "replace",
                path: "/password_exp_timestamp",
                value: final_date,
              },
              { op: "replace", path: "/email", value: data.email },
            ],
          };
    return editUser(id, operations);
  };

  const onSubmitHandler = async (
    values: UserDetailsFormData,
    actions: FormikHelpers<UserDetailsFormData>
  ) => {
    try {
      if (userData?.data.is_integration) mutate(await onSave(values));
      if (!userData?.data.is_integration) await deleteDevices();

      const updateMessage = userData?.data.is_integration
        ? t("validation.success.update")
        : t("validation.success.updateUser");

      setPreventNavigation(false);

      history.push("/settings/users");
      enqueueSnackbar(updateMessage, { variant: "success" });

      actions.setStatus({});
    } catch (error) {
      // eslint-disable-next-line @typescript-eslint/ban-ts-comment
      // @ts-ignore
      const errorCode = error?.response?.data?.error?.code;

      if (errorCode?.endsWith("user_already_exists")) {
        actions.setStatus({
          serverSideErrors: {
            username: t(`validation.unknownError.user_already_exists`),
          },
        });
        return;
      }
      if (errorCode?.endsWith("password_invalid_constraints")) {
        actions.setStatus({
          serverSideErrors: {
            password: t(`validation.unknownError.password_invalid_constraints`),
          },
        });
        return;
      }
      if (errorCode?.endsWith("password_missmatch")) {
        actions.setStatus({
          serverSideErrors: {
            old_password: t(`validation.unknownError.password_missmatch`),
          },
        });
        return;
      }

      setBannerErrorCode(errorCode);
      // eslint-disable-next-line @typescript-eslint/ban-ts-comment
      // @ts-ignore
      const errorStatus = error?.response?.status;
      // eslint-disable-next-line @typescript-eslint/ban-ts-comment
      // @ts-ignore
      const jsonError = error.toJSON();
      if (errorStatus === 405)
        setBannerErrorCode("service_not_available_update");
      if (jsonError.message === "Network Error")
        setBannerErrorCode("service_not_available_update");

      setShowBanner(true);
    }
  };
  const devices = useCallback((): Device[] => {
    const devicesArray: Device[] = [];
    Object.entries(
      (userData?.data.attributes as Record<string, UserDeviceAttribute>) || {}
    ).map(
      ([key, value]) =>
        key.includes("device") &&
        devicesArray.push({ device_id: value.id, device_name: value.name })
    );
    return devicesArray;
  }, [userData?.data.attributes]);

  const initialValues = useMemo(
    () => ({
      username: userData?.data.username || "",
      name: userData?.data.name || "",
      password: "",
      confirmation_password: "",
      old_password: "",
      enabled: userData?.data.enabled,
      group: joinGroups(userData?.data.groups_details) || "",
      email: userData?.data.email || "",
      expiration_date: new Date(userData?.data.password_exp_timestamp || ""),
      devices: devices(),
      expiration_time: {
        hours: parseInt(
          moment(
            getFormattedDateTimeWithTZ(
              userData?.data.password_exp_timestamp,
              timezone
            )
          )
            .format(standardDateFormat)
            .split(" ")[1]
            .split(":")[0],
          10
        ),
        minutes: parseInt(
          moment(
            getFormattedDateTimeWithTZ(
              userData?.data.password_exp_timestamp,
              timezone
            )
          )
            .format(standardDateFormat)
            .split(" ")[1]
            .split(":")[1],
          10
        ),
        seconds: parseInt(
          moment(
            getFormattedDateTimeWithTZ(
              userData?.data.password_exp_timestamp,
              timezone
            )
          )
            .format(standardDateFormat)
            .split(" ")[1]
            .split(":")[2],
          10
        ),
        period: formatAMPM(
          moment(
            getFormattedDateTimeWithTZ(
              userData?.data.password_exp_timestamp,
              timezone
            )
          )
            .format(standardDateFormat)
            .split(" ")[1]
        ),
      },
    }),
    [
      devices,
      timezone,
      userData?.data.email,
      userData?.data.enabled,
      userData?.data.groups_details,
      userData?.data.name,
      userData?.data.password_exp_timestamp,
      userData?.data.username,
    ]
  );

  const phones = (): UserPhoneAttribute[] => {
    const phoneArray: UserPhoneAttribute[] = [];
    Object.entries(
      (userData?.data.attributes as Record<string, UserPhoneAttribute>) || {}
    ).map(
      ([key, value]) =>
        key.includes("phone") &&
        phoneArray.push({ id: value.id, number: value.number })
    );
    return phoneArray;
  };

  const formHeaderTitle = `${
    userData?.data.name === "n/a" ? "" : userData?.data.name
  } ${userData?.data.surname === "n/a" ? "" : userData?.data.surname}`;
  const emailHeader =
    userData?.data.email === "n/a" ? "" : userData?.data.email;
  return (
    <Formik
      initialStatus={{ serverSideErrors: {} }}
      initialValues={initialValues}
      validationSchema={validationSchema}
      onSubmit={onSubmitHandler}
    >
      {(props) => {
        const { handleSubmit, isSubmitting } = props;

        const goBackHandler = () => {
          history.goBack();
        };

        const onBannerCloseHandler = () => {
          setShowBanner(false);
          setBannerErrorCode("");
        };

        return (
          <Container className={classes.container} maxWidth="xl">
            {isSubmitting && (
              <Loading
                delay={500}
                loadingLabel={
                  userData?.data.is_integration
                    ? t("submiting_update")
                    : t("submiting_user_update")
                }
                className={clsx(classes.loading, classes.zIndex)}
              />
            )}
            <Banner
              open={showBanner}
              variant="error"
              showIcon
              label={
                errorBannerCode
                  ? t(`validation.unknownError.${errorBannerCode}`)
                  : t(`validation.unknownError.add`)
              }
              onClose={onBannerCloseHandler}
            />
            <form onSubmit={handleSubmit} noValidate>
              <FormHeader
                title={formHeaderTitle === " " ? emailHeader : formHeaderTitle}
                icon={
                  <HvTooltip
                    style={{
                      position: "absolute",
                    }}
                    title={
                      <HvTypography>
                        {userData?.data.is_integration
                          ? t("tooltip.serviceAccount")
                          : t("tooltip.userAccount")}
                      </HvTypography>
                    }
                  >
                    <div>
                      {userData?.data.is_integration ? (
                        <Settings className={classes.icon} />
                      ) : (
                        <User className={classes.userIcon} />
                      )}
                    </div>
                  </HvTooltip>
                }
                sections={!userData?.data.is_integration ? sections : []}
                onCancel={goBackHandler}
              />
              <HvGrid container spacing={0}>
                <HvGrid xs={2} className={classes.firstLastColumn} />
                <HvGrid
                  container
                  spacing={0}
                  className={classes.form}
                  xs={8}
                  item
                >
                  <HvGrid
                    className={classes.sectionMargin}
                    tabIndex={-1}
                    id={sections[0].value}
                    item
                    xs={12}
                  >
                    <Details
                      mode="edit"
                      isIntegration={!!userData?.data.is_integration}
                      phones={phones()}
                    />
                  </HvGrid>
                  <HvGrid
                    className={classes.sectionMargin}
                    tabIndex={-1}
                    id={sections[1].value}
                    item
                    xs={12}
                  >
                    {!userData?.data.is_integration && (
                      <Devices
                        setRemovedDevices={setRemovedDevices}
                        removedDevices={removedDevices}
                      />
                    )}
                  </HvGrid>
                </HvGrid>
                <HvGrid
                  spacing={0}
                  xs={2}
                  className={classes.firstLastColumn}
                />
              </HvGrid>
            </form>
          </Container>
        );
      }}
    </Formik>
  );
};
export const UserEditAuth: React.FC = () => {
  const { hasModulePolicy } = useContext(UserDataContext);
  const hasRight = useMemo(() => {
    return hasModulePolicy(ModuleNames.core, ModulePolicyTypes.usergroupsAdmin);
  }, [hasModulePolicy]);
  return (
    <>
      {hasRight ? (
        <Container noHVContainer>
          <Edit />
        </Container>
      ) : (
        <NotFound />
      )}
    </>
  );
};
export default Edit;
